import React, { useState, useEffect, useRef } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Rocket, Sparkles, Plug, ListChecks, Users2, ShieldCheck, Check, Play,
  RefreshCcw, AlertTriangle, Plus, Database, FileText, Workflow,
  ArrowRight, ShieldAlert, CalendarCheck2,
} from "@/components/icons";
import { useStore } from "@/store/store";
import { PageHeader, SectionTitle } from "@/components/askilia/layout-bits";
import { SourceTag, Chip, IliaAvatar } from "@/components/askilia/atoms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { timeAgo } from "@/lib/format";
import { useI18n } from "@/i18n";
const TABS = [
  { id: "activation", labelKey: "admin.settings.tab.activation", icon: Rocket },
  { id: "ilia", labelKey: "admin.settings.tab.ilia", icon: Sparkles },
  { id: "channels", labelKey: "admin.settings.tab.channels", icon: Plug },
  { id: "services", labelKey: "admin.settings.tab.services", icon: ListChecks },
  { id: "team", labelKey: "admin.settings.tab.team", icon: Users2 },
  { id: "privacy", labelKey: "admin.settings.tab.privacy", icon: ShieldCheck },
];

const AUTO_OPTIONS = [
  { id: "off", labelKey: "admin.settings.sync.off" },
  { id: "hourly", labelKey: "admin.settings.sync.hourly" },
  { id: "6h", labelKey: "admin.settings.sync.sixHours" },
  { id: "daily", labelKey: "admin.settings.sync.daily" },
];

const IMPORTED_CLINIC_DEFAULTS = {
  legalName: "Vake Medical Clinic LLC",
  phone: "+995 32 2 11 07 07",
  email: "hello@vakeclinic.ge",
  website: "vakeclinic.ge",
  usp: "Specialist consultation and core diagnostics coordinated in one visit.",
  marketingAngle: "Get clear next steps without the friction and waiting of a large hospital.",
  growthDriver: "Cardiology diagnostics",
  flagshipOffer: "Cardiology consultation + ECG · ₾160",
  idealAudience: "Busy adults 35+ who want fast, understandable answers about heart symptoms.",
  proofPoints: "Same-day ECG, transparent list prices, Georgian and English support.",
};

const IMPORTED_LOCATION_DEFAULTS = [
  { id: "loc-vake", name: "Vake clinic", address: "17 Ilia Chavchavadze Avenue", city: "Tbilisi 0179", phone: "+995 32 2 11 07 07", hours: "Mon-Sat · 10:00-19:00" },
  { id: "loc-saburtalo", name: "Saburtalo diagnostics", address: "12 Pekini Avenue", city: "Tbilisi 0160", phone: "+995 32 2 11 07 08", hours: "Mon-Fri · 09:00-18:00" },
];

const IMPORTED_RESOURCE_DEFAULTS = {
  "r-nino": {
    photoUrl: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?auto=format&fit=crop&w=160&h=160&q=80",
    credentials: "MD · Cardiology",
    languages: "Georgian, English, Russian",
    profile: "Adult cardiology, rhythm concerns and preventive heart checks.",
  },
  "r-lika": {
    photoUrl: "https://images.unsplash.com/photo-1594824476967-48c8b964273f?auto=format&fit=crop&w=160&h=160&q=80",
    credentials: "MD · Dermatology",
    languages: "Georgian, English",
    profile: "Medical dermatology with a practical, plain-language care approach.",
  },
};

function clinicImportDraft(clinic) {
  return {
    ...IMPORTED_CLINIC_DEFAULTS,
    ...clinic,
    locations: clinic.locations?.length
      ? clinic.locations.map((location) => ({ ...location }))
      : IMPORTED_LOCATION_DEFAULTS.map((location) => ({ ...location })),
  };
}

function resourceImportDraft(resource) {
  return { ...(IMPORTED_RESOURCE_DEFAULTS[resource.id] || {}), ...resource };
}
const AUTO_LABEL_KEYS = {
  off: "admin.settings.sync.off",
  hourly: "admin.settings.sync.hourlyAdverb",
  "6h": "admin.settings.sync.sixHoursAdverb",
  daily: "admin.settings.sync.dailyAdverb",
};

export default function Settings() {
  const { tab } = useParams();
  const nav = useNavigate();
  const { t } = useI18n();
  const value = TABS.find((t) => t.id === tab) ? tab : "activation";

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 lg:px-8">
      <PageHeader
        title={t("admin.settings.title")}
        subtitle={t("admin.settings.subtitle")}
        actions={(
          <Button variant="outline" size="sm" onClick={() => nav("/onboarding")}>
            <Rocket className="mr-1.5 h-4 w-4" />
            {t("admin.settings.openOnboarding")}
          </Button>
        )}
      />
      <Tabs value={value} onValueChange={(v) => nav(`/settings/${v}`)} className="mt-6">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 bg-muted/60 p-1">
          {TABS.map((tabItem) => (
            <TabsTrigger key={tabItem.id} value={tabItem.id} className="gap-1.5 data-[state=active]:bg-surface">
              <tabItem.icon className="h-4 w-4" /> <span className="hidden sm:inline">{t(tabItem.labelKey)}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="activation" className="mt-5"><Activation /></TabsContent>
        <TabsContent value="ilia" className="mt-5"><IliaAutomations /></TabsContent>
        <TabsContent value="channels" className="mt-5"><Channels /></TabsContent>
        <TabsContent value="services" className="mt-5"><Services /></TabsContent>
        <TabsContent value="team" className="mt-5"><Team /></TabsContent>
        <TabsContent value="privacy" className="mt-5"><Privacy /></TabsContent>
      </Tabs>
    </div>
  );
}

function Activation() {
  const { state, actions } = useStore();
  const nav = useNavigate();
  const { t, localize } = useI18n();
  const steps = state.activation;
  const done = steps.filter((s) => s.done).length;
  const pct = Math.round((done / steps.length) * 100);
  const testDone = steps.find((s) => s.id === "act-7")?.done;
  const liveDone = steps.find((s) => s.id === "act-8")?.done;

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-border bg-surface p-5 shadow-xs rosehip-glow">
        <div className="flex items-center gap-3">
          <IliaAvatar size={40} active />
          <div className="flex-1">
            <p className="text-sm font-semibold text-foreground">{t("admin.settings.activation.progress", { percent: pct })}</p>
            <p className="text-xs text-muted-foreground">{t("admin.settings.activation.progressHint")}</p>
          </div>
        </div>
        <Progress value={pct} className="mt-4 h-2" />
      </div>

      <div className="space-y-2.5">
        {steps.map((s, i) => (
          <div key={s.id} className="flex items-center gap-3 rounded-xl border border-border bg-surface p-3.5 shadow-xs">
            <span className={"inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold " + (s.done ? "bg-success text-success-foreground" : "bg-muted text-muted-foreground")}>
              {s.done ? <Check className="h-4 w-4" /> : i + 1}
            </span>
            <div className="min-w-0 flex-1">
              <p className={"text-sm font-medium " + (s.done ? "text-foreground" : "text-foreground")}>{localize(s.label)}</p>
              <p className="text-xs text-muted-foreground">{localize(s.hint)}</p>
            </div>
            {!s.done && s.id === "act-7" && (
              <Button size="sm" onClick={() => { actions.completeActivation("act-7"); toast.success(t("admin.setup.testPassed"), { description: t("admin.setup.testPassedDescription") }); }}>
                <Play className="mr-1.5 h-4 w-4" /> {t("admin.settings.activation.runTest")}
              </Button>
            )}
            {!s.done && s.id === "act-8" && (
              <Button size="sm" disabled={!testDone} onClick={() => { actions.completeActivation("act-8"); toast.success(t("admin.settings.activation.liveToast"), { description: t("admin.settings.activation.liveToastDescription") }); }}>
                <Rocket className="mr-1.5 h-4 w-4" /> {t("admin.settings.activation.goLive")}
              </Button>
            )}
            {!s.done && !["act-7", "act-8"].includes(s.id) && (
              ["act-2", "act-3"].includes(s.id)
                ? <Button size="sm" variant="outline" onClick={() => nav("/setup")}><Database className="mr-1.5 h-4 w-4" /> {t("admin.settings.activation.openImport")}</Button>
                : <Button size="sm" variant="outline" onClick={() => actions.completeActivation(s.id)}>{t("admin.settings.activation.markDone")}</Button>
            )}
          </div>
        ))}
      </div>
      {liveDone && <div className="rounded-xl border border-success/40 bg-success-soft p-4 text-sm text-success-soft-foreground">{t("admin.settings.activation.liveCallout")}</div>}
    </div>
  );
}

function IliaAutomations() {
  const { state, actions } = useStore();
  const { t, localize } = useI18n();
  const activeAutomations = state.automations.filter((automation) => automation.active).length;

  const openPrototype = (area) => {
    toast(t("admin.settings.ilia.prototypeReady", { area }), {
      description: t("admin.settings.ilia.prototypeDescription"),
    });
  };

  return (
    <div className="space-y-5">
      <div className="overflow-hidden rounded-xl border border-primary/25 bg-surface shadow-xs rosehip-glow">
        <div className="flex items-start gap-3 bg-primary-soft/35 p-4">
          <IliaAvatar size={40} active />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-sm font-semibold text-foreground">{t("admin.settings.ilia.manage")}</h2>
              <Chip tone="success">{t("admin.settings.ilia.active")}</Chip>
            </div>
            <p className="mt-1 max-w-2xl text-xs leading-relaxed text-muted-foreground">
              {t("admin.settings.ilia.manageDescription")}
            </p>
          </div>
        </div>

        <div className="divide-y divide-border">
          <IliaManagementRow
            icon={FileText}
            title={t("admin.settings.ilia.masterPrompt")}
            description={t("admin.settings.ilia.masterPromptDescription")}
            status={t("admin.settings.ilia.configured")}
            action={t("admin.settings.ilia.openMasterPrompt")}
            onClick={() => openPrototype(t("admin.settings.ilia.masterPromptEditor"))}
          />
          <IliaManagementRow
            icon={Workflow}
            title={t("admin.settings.ilia.workflowBuilder")}
            description={t("admin.settings.ilia.workflowBuilderDescription")}
            status={t("admin.settings.ilia.activeCount", { count: activeAutomations })}
            action={t("admin.settings.ilia.openWorkflowBuilder")}
            onClick={() => openPrototype(t("admin.settings.ilia.workflowBuilder"))}
          />
        </div>
      </div>

      <IliaReleaseManager releases={state.iliaReleases} actions={actions} />

      <div className="rounded-xl border border-border bg-surface p-4 shadow-xs">
        <SectionTitle hint={t("admin.settings.ilia.automationsHint")}>{t("admin.settings.ilia.automations")}</SectionTitle>
        <div className="space-y-2.5">
          {state.automations.map((a) => (
            <div key={a.id} className="rounded-xl border border-border bg-background p-3.5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold text-foreground">{localize(a.name)}</p>
                    {a.tested ? <Chip tone="success">{t("admin.settings.ilia.tested")}</Chip> : <Chip tone="warning">{t("admin.settings.ilia.notTested")}</Chip>}
                  </div>
                  <div className="mt-2 grid gap-1 text-xs text-muted-foreground sm:grid-cols-3">
                    <p><span className="font-semibold text-foreground">{t("admin.settings.ilia.when")}</span> {localize(a.when)}</p>
                    <p><span className="font-semibold text-foreground">{t("admin.settings.ilia.if")}</span> {localize(a.cond)}</p>
                    <p><span className="font-semibold text-foreground">{t("admin.settings.ilia.then")}</span> {localize(a.then)}</p>
                  </div>
                </div>
                <Switch checked={a.active} onCheckedChange={() => actions.toggleAutomation(a.id)} />
              </div>
              {!a.tested && (
                <Button size="sm" variant="outline" className="mt-3" onClick={() => actions.testAutomation(a.id)}>
                  <Play className="mr-1.5 h-4 w-4" /> {t("admin.settings.ilia.testSample")}
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-border bg-surface p-4 shadow-xs">
        <SectionTitle hint={t("admin.settings.ilia.guardrailsHint")}>{t("admin.settings.ilia.guardrails")}</SectionTitle>
        <div className="divide-y divide-border rounded-lg border border-border bg-background">
          <GuardrailRow
            icon={CalendarCheck2}
            title={t("admin.settings.ilia.confirmationRule")}
            description={t("admin.settings.ilia.confirmationRuleDescription")}
            status={t("admin.settings.ilia.alwaysOn")}
          />
          <GuardrailRow
            icon={ShieldCheck}
            title={t("admin.settings.ilia.moneyRule")}
            description={t("admin.settings.ilia.moneyRuleDescription")}
            status={t("admin.settings.ilia.operatorReview")}
          />
          <GuardrailRow
            icon={ShieldAlert}
            title={t("admin.settings.ilia.riskRule")}
            description={t("admin.settings.ilia.riskRuleDescription")}
            status={t("admin.settings.ilia.immediate")}
          />
        </div>
        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-muted-foreground">{t("admin.settings.ilia.auditStatement")}</p>
          <Button size="sm" variant="outline" className="w-full whitespace-nowrap sm:w-auto" onClick={() => openPrototype(t("admin.settings.ilia.guardrailManager"))}>
            {t("admin.settings.ilia.reviewRules")} <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function IliaReleaseManager({ releases, actions }) {
  const { t, localize } = useI18n();
  const [restoreTarget, setRestoreTarget] = useState(null);
  const production = releases.versions.find((version) => version.id === releases.activeId);
  const staging = releases.versions.find((version) => version.id === releases.stagingId);
  const history = releases.versions
    .filter((version) => version.id !== releases.activeId && version.id !== releases.stagingId)
    .slice(0, 3);

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-surface shadow-xs">
      <div className="flex flex-col gap-3 border-b border-border p-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-foreground">{t("admin.settings.release.title")}</h3>
            <Chip tone="muted">{t("admin.settings.release.savedCount", { count: releases.versions.length })}</Chip>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{t("admin.settings.release.description")}</p>
        </div>
        {!staging ? (
          <Button size="sm" variant="outline" className="w-full whitespace-nowrap sm:w-auto" onClick={actions.createIliaStaging}>
            <Plus className="mr-1.5 h-4 w-4" /> {t("admin.settings.release.createStaging")}
          </Button>
        ) : null}
      </div>

      <div className="grid gap-px bg-border md:grid-cols-2">
        <VersionEnvironment
          label={t("admin.settings.release.production")}
          liveLabel={t("admin.settings.release.live")}
          version={production}
          tone="success"
          detail={t("admin.settings.release.productionDetail")}
        />
        <VersionEnvironment
          label={t("admin.settings.release.staging")}
          liveLabel={t("admin.settings.release.live")}
          version={staging}
          tone="warning"
          detail={staging ? t("admin.settings.release.stagingDetail") : t("admin.settings.release.noChanges")}
          actions={staging ? (
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => actions.testIliaRelease(staging.id)}>
                <Play className="mr-1.5 h-4 w-4" /> {staging.testedAt ? t("admin.settings.release.testAgain") : t("admin.settings.release.testVersion", { number: staging.number })}
              </Button>
              <Button size="sm" disabled={!staging.testedAt} onClick={() => actions.publishIliaRelease(staging.id)}>
                <Rocket className="mr-1.5 h-4 w-4" /> {t("admin.settings.release.publish")}
              </Button>
            </div>
          ) : null}
        />
      </div>

      <div className="p-4">
        <div className="flex items-baseline justify-between gap-3">
          <h4 className="text-xs font-semibold text-foreground">{t("admin.settings.release.history")}</h4>
          <p className="text-[11px] text-muted-foreground">{t("admin.settings.release.historyLimit")}</p>
        </div>
        {history.length ? (
          <div className="mt-3 divide-y divide-border rounded-lg border border-border bg-background">
            {history.map((version) => (
              <div key={version.id} className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-medium text-foreground">{t("admin.settings.release.version", { number: version.number })}</p>
                    {version.publishedAt ? <Chip tone="muted">{t("admin.settings.release.previouslyLive")}</Chip> : null}
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">{localize(version.name)} · {localize(timeAgo(version.createdAt))}</p>
                </div>
                <Button size="sm" variant="ghost" className="w-full whitespace-nowrap sm:w-auto" onClick={() => setRestoreTarget(version)}>
                  <RefreshCcw className="mr-1.5 h-4 w-4" /> {t("admin.settings.release.restoreToStaging")}
                </Button>
              </div>
            ))}
          </div>
        ) : <p className="mt-3 text-xs text-muted-foreground">{t("admin.settings.release.emptyHistory")}</p>}
      </div>

      <Dialog open={!!restoreTarget} onOpenChange={(open) => !open && setRestoreTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("admin.settings.release.restoreTitle", { number: restoreTarget?.number ?? "" })}</DialogTitle>
            <DialogDescription>
              {t("admin.settings.release.restoreDescription")}
            </DialogDescription>
          </DialogHeader>
          {staging ? <p className="rounded-lg border border-warning/35 bg-warning-soft p-3 text-xs text-warning-soft-foreground">{t("admin.settings.release.replaceWarning")}</p> : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreTarget(null)}>{t("admin.cancel")}</Button>
            <Button onClick={() => { actions.restoreIliaRelease(restoreTarget.id); setRestoreTarget(null); }}>{t("admin.settings.release.restoreToStaging")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function VersionEnvironment({ label, liveLabel, version, tone, detail, actions }) {
  const { t, localize } = useI18n();
  return (
    <div className="bg-surface p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{label}</p>
          <p className="mt-1 text-base font-semibold text-foreground">{version ? t("admin.settings.release.version", { number: version.number }) : t("admin.settings.release.noVersion")}</p>
        </div>
        {version ? <Chip tone={tone}>{label === t("admin.settings.release.production") ? liveLabel : version.testedAt ? t("admin.settings.release.testPassed") : t("admin.settings.ilia.notTested")}</Chip> : null}
      </div>
      {version ? <p className="mt-2 text-sm font-medium text-foreground">{localize(version.name)}</p> : null}
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{version?.summary ? localize(version.summary) : detail}</p>
      {version ? <p className="mt-2 text-[11px] text-muted-foreground">{detail}</p> : null}
      {actions}
    </div>
  );
}

function IliaManagementRow({ icon: Icon, title, description, status, action, onClick }) {
  return (
    <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary-soft-foreground">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-semibold text-foreground">{title}</p>
          <Chip tone="primary">{status}</Chip>
        </div>
        <p className="mt-1 max-w-2xl text-xs leading-relaxed text-muted-foreground">{description}</p>
      </div>
      <Button variant="outline" size="sm" className="w-full whitespace-nowrap sm:w-auto" onClick={onClick}>
        {action} <ArrowRight className="ml-1.5 h-4 w-4" />
      </Button>
    </div>
  );
}

function GuardrailRow({ icon: Icon, title, description, status }) {
  return (
    <div className="flex items-start gap-3 p-3.5">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
      <div className="min-w-0 flex-1 sm:flex sm:items-start sm:justify-between sm:gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">{title}</p>
          <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{description}</p>
        </div>
        <div className="mt-2 shrink-0 sm:mt-0"><Chip tone="muted">{status}</Chip></div>
      </div>
    </div>
  );
}

function Channels() {
  const { state, actions } = useStore();
  const nav = useNavigate();
  const location = useLocation();
  const { t, localize, formatMoney } = useI18n();
  const [syncing, setSyncing] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(() => new URLSearchParams(location.search).get("review") === "imported");
  const actionsRef = useRef(actions);
  actionsRef.current = actions;
  const erp = state.erp || {};
  const pending = erp.pendingChanges || [];
  const svcName = (id) => localize(state.services.find((s) => s.id === id)?.name || id);

  useEffect(() => {
    if (new URLSearchParams(location.search).get("review") === "imported") setReviewOpen(true);
  }, [location.search]);

  const runSync = () => {
    const count = pending.length;
    setSyncing(true);
    setTimeout(() => {
      actions.syncErp();
      setSyncing(false);
      if (count > 0) toast.success(t("admin.settings.channels.updatesPulled", { count }), { description: t("admin.settings.channels.pricesCurrent") });
      else toast(t("admin.settings.channels.upToDate"), { description: t("admin.settings.channels.noChanges") });
    }, 1100);
  };

  // Auto-sync: when enabled and the ERP has drifted, Ilia pulls hands-free.
  useEffect(() => {
    if (erp.autoSync && erp.autoSync !== "off" && pending.length > 0 && !syncing) {
      const count = pending.length;
      const id = setTimeout(() => {
        actionsRef.current.syncErp();
        toast.success(t("admin.settings.channels.autoUpdatesPulled", { count }), { description: t("admin.settings.channels.autoPricesFresh", { schedule: t(AUTO_LABEL_KEYS[erp.autoSync]) }) });
      }, 1500);
      return () => clearTimeout(id);
    }
  }, [erp.autoSync, pending.length, syncing, t]);

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-3 rounded-xl border border-primary/30 bg-primary-soft/40 p-4 sm:flex-row sm:items-center">
        <div className="flex-1">
          <p className="text-sm font-semibold text-primary-soft-foreground">{t("admin.settings.channels.bringData")}</p>
          <p className="text-xs text-primary-soft-foreground/90">
            {erp.connected ? t("admin.settings.channels.connectedDescription", { name: erp.name }) : t("admin.settings.channels.initialDescription")}
          </p>
        </div>
        <Button onClick={() => nav("/setup")}>
          <Database className="mr-1.5 h-4 w-4" /> {erp.connected ? t("admin.settings.channels.rerunImport") : t("admin.settings.channels.connectSystem")}
        </Button>
      </div>

      {erp.connected && (
        <div className="rounded-xl border border-border bg-surface p-4 shadow-xs">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-muted"><Database className="h-4 w-4" /></span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-foreground">{erp.name}</p>
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className={"h-1.5 w-1.5 rounded-full " + (pending.length ? "bg-warning" : "bg-success")} />
                {syncing ? t("admin.settings.channels.syncing") : t("admin.settings.channels.lastSynced", { time: localize(timeAgo(erp.lastSync)) })}
                <span className="text-muted-foreground/60">·</span> {t("admin.settings.channels.serviceCount", { count: state.services.length })}
              </p>
            </div>
            <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
              <Button size="sm" variant="outline" onClick={() => setReviewOpen(true)}>
                <FileText className="mr-1.5 h-4 w-4" /> {t("admin.settings.channels.reviewImported")}
              </Button>
              <Button size="sm" variant={pending.length ? "default" : "outline"} onClick={runSync} disabled={syncing}>
                <RefreshCcw className={"mr-1.5 h-4 w-4 " + (syncing ? "animate-spin" : "")} />
                {pending.length ? t("admin.settings.channels.pullChanges", { count: pending.length }) : t("admin.settings.channels.syncNow")}
              </Button>
            </div>
          </div>

          <div className="mt-3 flex flex-col gap-2 rounded-lg border border-border bg-background p-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-medium text-foreground">{t("admin.settings.channels.autoSync")}</p>
              <p className="text-xs text-muted-foreground">
                {erp.autoSync === "off"
                  ? t("admin.settings.channels.autoSyncOff")
                  : t("admin.settings.channels.autoSyncOn", { schedule: t(AUTO_LABEL_KEYS[erp.autoSync]) })}
              </p>
            </div>
            <Select value={erp.autoSync || "off"} onValueChange={(v) => { actions.setAutoSync(v); if (v !== "off") toast(t("admin.settings.channels.autoSyncToast", { schedule: t(AUTO_LABEL_KEYS[v]) }), { description: t("admin.settings.channels.autoSyncToastDescription") }); }}>
              <SelectTrigger className="w-full sm:w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                {AUTO_OPTIONS.map((o) => <SelectItem key={o.id} value={o.id}>{t(o.labelKey)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {pending.length > 0 && !syncing && (
            <div className="mt-3 space-y-1.5 rounded-lg border border-warning/30 bg-warning-soft/60 p-3">
              <p className="text-xs font-medium text-warning-soft-foreground">{t("admin.settings.channels.priceChanges", { name: erp.name })}</p>
              {pending.map((c) => (
                <div key={c.serviceId} className="flex items-center justify-between text-sm">
                  <span className="text-foreground">{svcName(c.serviceId)}</span>
                  <span className="font-sans text-xs font-medium text-muted-foreground tabular-nums">
                    <span className="line-through">{formatMoney(c.from)}</span> <span className="text-foreground">→ {formatMoney(c.to)}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {state.channels.map((c) => (
        <div key={c.id} className="flex items-center gap-3 rounded-xl border border-border bg-surface p-4 shadow-xs">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-foreground">{localize(c.name)}</p>
            <p className="text-xs text-muted-foreground">{c.handle}</p>
          </div>
          {c.status === "connected" ? (
            <Chip tone="success">{t("admin.settings.channels.connected")}</Chip>
          ) : (
            <div className="flex items-center gap-2">
              <Chip tone="destructive"><AlertTriangle className="h-3.5 w-3.5" /> {t("admin.settings.channels.expired")}</Chip>
              <Button size="sm" variant="outline" onClick={() => actions.connectChannel(c.id)}><RefreshCcw className="mr-1.5 h-4 w-4" /> {t("admin.settings.channels.reconnect")}</Button>
            </div>
          )}
        </div>
      ))}
      <Button variant="outline" className="w-full" onClick={() => nav("/setup")}>
        <Plus className="mr-1.5 h-4 w-4" /> {t("admin.settings.channels.connectAnother")}
      </Button>

      <ImportedDataSheet open={reviewOpen} onOpenChange={setReviewOpen} />
    </div>
  );
}

function ImportedDataSheet({ open, onOpenChange }) {
  const { state, actions } = useStore();
  const { t, localize } = useI18n();
  const initializedForOpen = useRef(false);
  const [manualServiceOpen, setManualServiceOpen] = useState(false);
  const [draft, setDraft] = useState(() => ({
    clinic: clinicImportDraft(state.clinic),
    resources: state.resources.map(resourceImportDraft),
    services: state.services.map((item) => ({ ...item })),
    knowledgePages: (state.onboarding.knowledgePages || []).map((item) => ({ ...item })),
  }));

  useEffect(() => {
    if (!open) {
      initializedForOpen.current = false;
      setManualServiceOpen(false);
      return;
    }
    if (initializedForOpen.current) return;
    initializedForOpen.current = true;
    setDraft({
      clinic: clinicImportDraft(state.clinic),
      resources: state.resources.map(resourceImportDraft),
      services: state.services.map((item) => ({ ...item })),
      knowledgePages: (state.onboarding.knowledgePages || []).map((item) => ({ ...item })),
    });
  }, [open, state.clinic, state.resources, state.services, state.onboarding.knowledgePages]);

  const updateClinic = (key, value) => setDraft((current) => ({
    ...current,
    clinic: { ...current.clinic, [key]: value },
  }));
  const updateLocation = (id, key, value) => setDraft((current) => ({
    ...current,
    clinic: {
      ...current.clinic,
      locations: current.clinic.locations.map((location) => location.id === id ? { ...location, [key]: value } : location),
    },
  }));
  const updateResource = (id, key, value) => setDraft((current) => ({
    ...current,
    resources: current.resources.map((item) => item.id === id ? { ...item, [key]: value } : item),
  }));
  const updateService = (id, key, value) => setDraft((current) => ({
    ...current,
    services: current.services.map((item) => item.id === id ? { ...item, [key]: value } : item),
  }));
  const updateKnowledgePage = (id, key, value) => setDraft((current) => ({
    ...current,
    knowledgePages: current.knowledgePages.map((item) => item.id === id ? { ...item, [key]: value } : item),
  }));
  const addManualService = (fields) => {
    const service = actions.addManualService(fields);
    setDraft((current) => ({
      ...current,
      services: [...current.services, service],
    }));
    setManualServiceOpen(false);
  };
  const save = () => {
    actions.saveImportedData(draft);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex !w-[min(100vw,768px)] !max-w-none flex-col overflow-hidden p-0">
        <SheetHeader className="border-b border-border px-5 py-5 pr-12">
          <div className="flex flex-wrap items-center gap-2">
            <SheetTitle>{t("admin.settings.import.reviewTitle", { name: state.erp?.name || t("admin.settings.import.clinicSystem") })}</SheetTitle>
            <SourceTag source="imported" />
          </div>
          <SheetDescription>
            {t("admin.settings.import.reviewDescription")}
          </SheetDescription>
        </SheetHeader>

        <Tabs defaultValue="business" className="flex min-h-0 flex-1 flex-col">
          <div className="overflow-x-auto border-b border-border px-5 py-3 scroll-thin">
            <TabsList className="h-auto w-max min-w-full justify-start bg-muted/60 p-1">
              <TabsTrigger value="business">{t("admin.settings.import.tabBusiness")}</TabsTrigger>
              <TabsTrigger value="services">{t("admin.settings.import.tabServices", { count: draft.services.length })}</TabsTrigger>
              <TabsTrigger value="resources">{t("admin.settings.import.tabResources", { count: draft.resources.length })}</TabsTrigger>
              <TabsTrigger value="knowledge">{t("admin.settings.import.tabKnowledge", { count: draft.knowledgePages.length })}</TabsTrigger>
            </TabsList>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 scroll-thin">
            <TabsContent value="business" className="mt-0">
              <div className="space-y-6">
                <ImportSection title={t("admin.settings.import.businessDetails")} description={t("admin.settings.import.businessDescription", { name: state.erp?.name || t("admin.settings.import.yourClinicSystem") })} source="imported">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <ImportedField label={t("admin.settings.import.productName")} value={draft.clinic.name} onChange={(value) => updateClinic("name", value)} />
                    <ImportedField label={t("admin.settings.import.legalName")} value={draft.clinic.legalName} onChange={(value) => updateClinic("legalName", value)} />
                    <ImportedField label={t("admin.settings.import.workspace")} value={draft.clinic.workspace} onChange={(value) => updateClinic("workspace", value)} />
                    <ImportedField label={t("admin.settings.import.mainPhone")} value={draft.clinic.phone} onChange={(value) => updateClinic("phone", value)} />
                    <ImportedField label={t("admin.settings.import.email")} value={draft.clinic.email} onChange={(value) => updateClinic("email", value)} />
                    <ImportedField label={t("admin.settings.import.website")} value={draft.clinic.website} onChange={(value) => updateClinic("website", value)} />
                    <ImportedField label={t("admin.settings.import.timezone")} value={draft.clinic.timezone} onChange={(value) => updateClinic("timezone", value)} />
                    <ImportedField label={t("admin.settings.import.currency")} value={draft.clinic.currency} onChange={(value) => updateClinic("currency", value)} />
                  </div>
                </ImportSection>

                <ImportSection title={t("admin.settings.import.locations")} description={t("admin.settings.import.locationsDescription")} source="imported">
                  <div className="space-y-3">
                    {draft.clinic.locations.map((location) => (
                      <div key={location.id} className="rounded-xl border border-border bg-background p-4">
                        <div className="grid gap-4 sm:grid-cols-2">
                          <ImportedField label={t("admin.settings.import.clinicName")} value={location.name} onChange={(value) => updateLocation(location.id, "name", value)} />
                          <ImportedField label={t("admin.settings.import.streetAddress")} value={location.address} onChange={(value) => updateLocation(location.id, "address", value)} />
                          <ImportedField label={t("admin.settings.import.cityPostcode")} value={location.city} onChange={(value) => updateLocation(location.id, "city", value)} />
                          <ImportedField label={t("admin.settings.import.clinicPhone")} value={location.phone} onChange={(value) => updateLocation(location.id, "phone", value)} />
                          <div className="sm:col-span-2">
                            <ImportedField label={t("admin.setup.openingHours")} value={location.hours} onChange={(value) => updateLocation(location.id, "hours", value)} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ImportSection>

                <ImportSection title={t("admin.settings.import.commercialContext")} description={t("admin.settings.import.commercialDescription")} source="staff">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <ImportedTextarea label={t("admin.settings.import.usp")} value={draft.clinic.usp} onChange={(value) => updateClinic("usp", value)} />
                    <ImportedTextarea label={t("admin.settings.import.marketingAngle")} value={draft.clinic.marketingAngle} onChange={(value) => updateClinic("marketingAngle", value)} />
                    <ImportedField label={t("admin.settings.import.growthDriver")} value={draft.clinic.growthDriver} onChange={(value) => updateClinic("growthDriver", value)} />
                    <ImportedField label={t("admin.settings.import.flagshipOffer")} value={draft.clinic.flagshipOffer} onChange={(value) => updateClinic("flagshipOffer", value)} />
                    <ImportedTextarea label={t("admin.settings.import.idealCustomer")} value={draft.clinic.idealAudience} onChange={(value) => updateClinic("idealAudience", value)} />
                    <ImportedTextarea label={t("admin.settings.import.proofPoints")} value={draft.clinic.proofPoints} onChange={(value) => updateClinic("proofPoints", value)} />
                  </div>
                </ImportSection>
              </div>
            </TabsContent>

            <TabsContent value="services" className="mt-0 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">{t("admin.settings.import.servicesPricing")}</h3>
                  <p className="mt-0.5 text-xs text-muted-foreground">{t("admin.settings.import.servicesPricingDescription")}</p>
                </div>
                <Button size="sm" type="button" onClick={() => setManualServiceOpen(true)}>
                  <Plus className="h-4 w-4" /> {t("admin.settings.import.addServiceManually")}
                </Button>
              </div>
              {draft.services.map((service) => (
                <section key={service.id} className="rounded-xl border border-border bg-surface p-4">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-foreground">{localize(service.name)}</p>
                      <p className="text-[11px] font-medium text-muted-foreground">{service.id}</p>
                    </div>
                    <SourceTag source={service.source} />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <ImportedField label={t("admin.settings.import.serviceName")} value={service.name} onChange={(value) => updateService(service.id, "name", value)} />
                    <ImportedField label={t("admin.settings.import.priceGel")} type="number" value={service.price} onChange={(value) => updateService(service.id, "price", Number(value))} />
                    <ImportedField label={t("admin.settings.import.durationMinutes")} type="number" value={service.duration} onChange={(value) => updateService(service.id, "duration", Number(value))} />
                    <ImportedField label={t("admin.settings.import.bufferMinutes")} type="number" value={service.buffer} onChange={(value) => updateService(service.id, "buffer", Number(value))} />
                    <ImportedSelect label={t("admin.settings.import.primaryResource")} value={service.resource || "none"} items={draft.resources} onChange={(value) => updateService(service.id, "resource", value === "none" ? null : value)} />
                    <ImportedSelect label={t("admin.settings.import.additionalRoom")} value={service.room || "none"} items={draft.resources} onChange={(value) => updateService(service.id, "room", value === "none" ? null : value)} allowNone />
                    <div className="sm:col-span-2">
                      <Label htmlFor={`description-${service.id}`}>{t("admin.settings.import.plainDescription")}</Label>
                      <Textarea id={`description-${service.id}`} value={service.description || ""} onChange={(event) => updateService(service.id, "description", event.target.value)} rows={2} className="mt-2 resize-none bg-background" />
                      <p className="mt-1 text-xs text-muted-foreground">{t("admin.settings.import.plainDescriptionHint")}</p>
                    </div>
                  </div>
                </section>
              ))}
            </TabsContent>

            <TabsContent value="resources" className="mt-0 space-y-4">
              {draft.resources.map((resource) => (
                <section key={resource.id} className="rounded-xl border border-border bg-surface p-4">
                  <div className="mb-4 flex items-center gap-3">
                    {resource.type === "person" ? (
                      <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-xl border border-border bg-muted">
                        <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-muted-foreground">{resource.short?.replace("Dr. ", "").slice(0, 2)}</span>
                        {resource.photoUrl ? <img src={resource.photoUrl} alt={t("admin.settings.import.profileAlt", { name: localize(resource.name) })} className="relative h-full w-full object-cover" onError={(event) => { event.currentTarget.style.display = "none"; }} /> : null}
                      </div>
                    ) : null}
                    <div>
                      <p className="text-sm font-semibold text-foreground">{localize(resource.name)}</p>
                      <p className="text-[11px] font-medium text-muted-foreground">{resource.id}</p>
                    </div>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <ImportedField label={t("admin.settings.import.fullName")} value={resource.name} onChange={(value) => updateResource(resource.id, "name", value)} />
                    <ImportedField label={t("admin.settings.import.calendarLabel")} value={resource.short} onChange={(value) => updateResource(resource.id, "short", value)} />
                    <ImportedField label={t("admin.settings.import.roleRoomDetails")} value={resource.role} onChange={(value) => updateResource(resource.id, "role", value)} />
                    {resource.type === "person" ? <ImportedField label={t("admin.settings.import.photoUrl")} value={resource.photoUrl} onChange={(value) => updateResource(resource.id, "photoUrl", value)} /> : null}
                    {resource.type === "person" ? <ImportedField label={t("admin.settings.import.credentials")} value={resource.credentials} onChange={(value) => updateResource(resource.id, "credentials", value)} /> : null}
                    {resource.type === "person" ? <ImportedField label={t("admin.settings.import.languages")} value={resource.languages} onChange={(value) => updateResource(resource.id, "languages", value)} /> : null}
                    <div className="space-y-2">
                      <Label htmlFor={`resource-type-${resource.id}`}>{t("admin.settings.import.resourceType")}</Label>
                      <Select value={resource.type} onValueChange={(value) => updateResource(resource.id, "type", value)}>
                        <SelectTrigger id={`resource-type-${resource.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="person">{t("admin.settings.import.person")}</SelectItem>
                          <SelectItem value="room">{t("admin.settings.import.room")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {resource.type === "person" ? (
                      <div className="sm:col-span-2">
                        <ImportedTextarea label={t("admin.settings.import.profileSummary")} value={resource.profile} onChange={(value) => updateResource(resource.id, "profile", value)} />
                      </div>
                    ) : null}
                  </div>
                </section>
              ))}
            </TabsContent>

            <TabsContent value="knowledge" className="mt-0 space-y-4">
              {draft.knowledgePages.length ? draft.knowledgePages.map((page) => (
                <section key={page.id} className="rounded-xl border border-border bg-surface p-4">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-foreground">{localize(page.title)}</p>
                      <p className="truncate text-[11px] font-medium text-muted-foreground">{page.url}</p>
                    </div>
                    <Chip tone="muted">{t("admin.settings.import.prototypePreview")}</Chip>
                  </div>
                  <div className="space-y-4">
                    <ImportedField label={t("admin.settings.import.pageTitle")} value={page.title} onChange={(value) => updateKnowledgePage(page.id, "title", value)} />
                    <ImportedField label={t("admin.settings.import.sourceUrl")} value={page.url} onChange={(value) => updateKnowledgePage(page.id, "url", value)} />
                    <ImportedTextarea label={t("admin.settings.import.knowledgeSummary")} value={page.summary} onChange={(value) => updateKnowledgePage(page.id, "summary", value)} />
                  </div>
                </section>
              )) : (
                <div className="rounded-xl border border-dashed border-border bg-background p-6 text-center">
                  <p className="text-sm font-medium text-foreground">{t("admin.settings.import.noKnowledge")}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{t("admin.settings.import.noKnowledgeHint")}</p>
                </div>
              )}
            </TabsContent>
          </div>
        </Tabs>

        <SheetFooter className="border-t border-border bg-surface px-5 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("admin.cancel")}</Button>
          <Button onClick={save}>{t("admin.settings.import.saveFields")}</Button>
        </SheetFooter>

        <ManualServiceDialog
          open={manualServiceOpen}
          onOpenChange={setManualServiceOpen}
          resources={draft.resources}
          onSave={addManualService}
        />
      </SheetContent>
    </Sheet>
  );
}

function emptyManualService() {
  return {
    name: "",
    price: "",
    duration: "30",
    buffer: "10",
    resource: "",
    room: "none",
    description: "",
  };
}

function ManualServiceDialog({ open, onOpenChange, resources, onSave }) {
  const { t, localize } = useI18n();
  const [form, setForm] = useState(emptyManualService);
  const [errors, setErrors] = useState({});
  const rooms = resources.filter((resource) => resource.type === "room");

  useEffect(() => {
    if (!open) return;
    setForm(emptyManualService());
    setErrors({});
  }, [open]);

  const update = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const submit = (event) => {
    event.preventDefault();
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = t("admin.settings.manual.nameError");
    if (form.price === "" || !Number.isFinite(Number(form.price)) || Number(form.price) < 0) nextErrors.price = t("admin.settings.manual.priceError");
    if (form.duration === "" || !Number.isFinite(Number(form.duration)) || Number(form.duration) <= 0) nextErrors.duration = t("admin.settings.manual.durationError");
    if (form.buffer === "" || !Number.isFinite(Number(form.buffer)) || Number(form.buffer) < 0) nextErrors.buffer = t("admin.settings.manual.bufferError");
    if (!form.resource) nextErrors.resource = t("admin.settings.manual.resourceError");
    if (form.room !== "none" && form.room === form.resource) nextErrors.room = t("admin.settings.manual.roomError");
    if (!form.description.trim()) nextErrors.description = t("admin.settings.manual.descriptionError");
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    onSave({
      ...form,
      room: form.room === "none" ? null : form.room,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto" overlayClassName="bg-foreground/25 backdrop-blur-[2px]">
        <DialogHeader>
          <DialogTitle>{t("admin.settings.import.addServiceManually")}</DialogTitle>
          <DialogDescription>
            {t("admin.settings.manual.description")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5" noValidate>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="manual-service-name">{t("admin.settings.import.serviceName")} <span className="text-destructive">*</span></Label>
              <Input
                id="manual-service-name"
                autoFocus
                value={form.name}
                onChange={(event) => update("name", event.target.value)}
                aria-invalid={Boolean(errors.name)}
                aria-describedby={errors.name ? "manual-service-name-error" : undefined}
                placeholder={t("admin.settings.manual.namePlaceholder")}
              />
              {errors.name ? <p id="manual-service-name-error" className="text-xs text-destructive">{errors.name}</p> : null}
            </div>

            <ManualNumberField
              id="manual-service-price"
              label={t("admin.settings.import.priceGel")}
              value={form.price}
              onChange={(value) => update("price", value)}
              error={errors.price}
              min="0"
              step="0.01"
              placeholder="120"
            />
            <ManualNumberField
              id="manual-service-duration"
              label={t("admin.settings.import.durationMinutes")}
              value={form.duration}
              onChange={(value) => update("duration", value)}
              error={errors.duration}
              min="1"
              step="1"
            />
            <ManualNumberField
              id="manual-service-buffer"
              label={t("admin.settings.import.bufferMinutes")}
              value={form.buffer}
              onChange={(value) => update("buffer", value)}
              error={errors.buffer}
              min="0"
              step="1"
            />

            <div className="space-y-2">
              <Label htmlFor="manual-service-resource">{t("admin.settings.import.primaryResource")} <span className="text-destructive">*</span></Label>
              <Select value={form.resource} onValueChange={(value) => update("resource", value)}>
                <SelectTrigger id="manual-service-resource" aria-invalid={Boolean(errors.resource)} aria-describedby={errors.resource ? "manual-service-resource-error" : undefined}>
                  <SelectValue placeholder={t("admin.settings.manual.chooseResource")} />
                </SelectTrigger>
                <SelectContent>
                  {resources.map((resource) => <SelectItem key={resource.id} value={resource.id}>{localize(resource.short)}</SelectItem>)}
                </SelectContent>
              </Select>
              {errors.resource ? <p id="manual-service-resource-error" className="text-xs text-destructive">{errors.resource}</p> : null}
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="manual-service-room">{t("admin.settings.import.additionalRoom")} <span className="font-normal text-muted-foreground">({t("admin.optional")})</span></Label>
              <Select value={form.room} onValueChange={(value) => update("room", value)}>
                <SelectTrigger id="manual-service-room" aria-invalid={Boolean(errors.room)} aria-describedby={errors.room ? "manual-service-room-error" : undefined}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("admin.settings.manual.noAdditionalRoom")}</SelectItem>
                  {rooms.map((room) => <SelectItem key={room.id} value={room.id}>{localize(room.short)}</SelectItem>)}
                </SelectContent>
              </Select>
              {errors.room ? <p id="manual-service-room-error" className="text-xs text-destructive">{errors.room}</p> : null}
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="manual-service-description">{t("admin.settings.import.plainDescription")} <span className="text-destructive">*</span></Label>
              <Textarea
                id="manual-service-description"
                value={form.description}
                onChange={(event) => update("description", event.target.value)}
                aria-invalid={Boolean(errors.description)}
                aria-describedby={errors.description ? "manual-service-description-error" : "manual-service-description-help"}
                rows={3}
                className="resize-none"
                placeholder={t("admin.settings.manual.descriptionPlaceholder")}
              />
              {errors.description
                ? <p id="manual-service-description-error" className="text-xs text-destructive">{errors.description}</p>
                : <p id="manual-service-description-help" className="text-xs text-muted-foreground">{t("admin.settings.manual.descriptionHint")}</p>}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t("admin.cancel")}</Button>
            <Button type="submit">{t("admin.settings.manual.addService")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ManualNumberField({ id, label, value, onChange, error, ...inputProps }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label} <span className="text-destructive">*</span></Label>
      <Input
        id={id}
        type="number"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : undefined}
        {...inputProps}
      />
      {error ? <p id={`${id}-error`} className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

function ImportedField({ label, value, onChange, type = "text" }) {
  const id = React.useId();
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} type={type} value={value ?? ""} onChange={(event) => onChange(event.target.value)} className="bg-background" />
    </div>
  );
}

function ImportedTextarea({ label, value, onChange }) {
  const id = React.useId();
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Textarea id={id} value={value ?? ""} onChange={(event) => onChange(event.target.value)} rows={3} className="resize-none bg-background" />
    </div>
  );
}

function ImportSection({ title, description, source, children }) {
  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border pb-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
        </div>
        <SourceTag source={source} />
      </div>
      {children}
    </section>
  );
}

function ImportedSelect({ label, value, items, onChange, allowNone = false }) {
  const id = React.useId();
  const { t, localize } = useI18n();
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id}><SelectValue /></SelectTrigger>
        <SelectContent>
          {allowNone ? <SelectItem value="none">{t("admin.settings.import.none")}</SelectItem> : null}
          {items.map((item) => <SelectItem key={item.id} value={item.id}>{localize(item.short)}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function Services() {
  const { state } = useStore();
  const { t, localize, formatMoney } = useI18n();
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-surface shadow-xs">
      <table className="w-full text-sm">
        <thead className="border-b border-border bg-muted/50 text-left text-xs text-muted-foreground">
          <tr>
            <th className="px-4 py-2.5 font-medium">{t("admin.settings.services.service")}</th>
            <th className="px-4 py-2.5 font-medium">{t("admin.settings.services.duration")}</th>
            <th className="px-4 py-2.5 font-medium">{t("admin.settings.services.price")}</th>
            <th className="px-4 py-2.5 font-medium">{t("admin.settings.services.source")}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {state.services.map((s) => {
            const res = state.resources.find((r) => r.id === s.resource);
            return (
              <tr key={s.id} className="hover:bg-muted/40">
                <td className="px-4 py-3">
                  <p className="font-medium text-foreground">{localize(s.name)}</p>
                  <p className="text-xs text-muted-foreground">{localize(res?.short)} · {t("admin.settings.services.buffer", { minutes: s.buffer })}</p>
                </td>
                <td className="px-4 py-3 font-sans text-muted-foreground tabular-nums">{t("admin.settings.services.minutes", { minutes: s.duration })}</td>
                <td className="px-4 py-3 font-medium text-foreground">{formatMoney(s.price)}</td>
                <td className="px-4 py-3">
                  <SourceTag source={s.source} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Team() {
  const { state } = useStore();
  const { t, localize } = useI18n();
  return (
    <div className="space-y-3">
      {state.team.map((m) => (
        <div key={m.id} className="flex items-center gap-3 rounded-xl border border-border bg-surface p-4 shadow-xs">
          <span className="relative inline-flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-sm font-semibold text-secondary-foreground">
            {m.initials}
            <span className={"absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full ring-2 ring-surface " + (m.online ? "bg-success" : "bg-muted-foreground/40")} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-foreground">{localize(m.name)}</p>
            <p className="text-xs text-muted-foreground">{m.email}</p>
          </div>
          <Chip tone={m.role === "Owner" ? "primary" : m.role === "Manager" ? "human" : "muted"}>{localize(m.role)}</Chip>
        </div>
      ))}
      <Button variant="outline" className="w-full" onClick={() => toast(t("admin.settings.team.inviteSent"), { description: t("admin.settings.team.inviteDescription") })}>
        <Plus className="mr-1.5 h-4 w-4" /> {t("admin.settings.team.invite")}
      </Button>
    </div>
  );
}

function Privacy() {
  const { t } = useI18n();
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-surface p-4 shadow-xs">
        <SectionTitle>{t("admin.settings.privacy.regionRetention")}</SectionTitle>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">{t("admin.settings.privacy.region")}</label>
            <Select defaultValue="eu">
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="eu">{t("admin.settings.privacy.eu")}</SelectItem>
                <SelectItem value="ge">{t("admin.settings.privacy.georgia")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">{t("admin.settings.privacy.retention")}</label>
            <Select defaultValue="365">
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="90">{t("admin.settings.privacy.days90")}</SelectItem>
                <SelectItem value="365">{t("admin.settings.privacy.months12")}</SelectItem>
                <SelectItem value="0">{t("admin.settings.privacy.untilDeleted")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      <div className="rounded-xl border border-border bg-surface p-4 shadow-xs">
        <SectionTitle>{t("admin.settings.privacy.consent")}</SectionTitle>
        <p className="text-sm text-muted-foreground">{t("admin.settings.privacy.consentDescription")}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => toast(t("admin.settings.privacy.exportPrepared"), { description: t("admin.settings.privacy.exportDescription") })}>{t("admin.settings.privacy.exportData")}</Button>
          <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive-soft" onClick={() => toast(t("admin.settings.privacy.deletionRequested"), { description: t("admin.settings.privacy.deletionDescription") })}>{t("admin.settings.privacy.requestDeletion")}</Button>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">{t("admin.settings.privacy.prototypeNote")}</p>
      </div>
    </div>
  );
}
